<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import {
  methodItemCreate,
  methodItemDelete,
  methodItemList,
  methodItemUpdate,
  methodItemShow,
  methodItemUpload,
  methodItemModelCSV,
} from "@/common/api/rest/method/method-item";

import { methodFieldList } from "@/common/api/rest/method/method-field";

import type {
  MethodItemType,
  MethodItemPayloadType,
} from "@/common/types/method/method-item";
import type { FileType } from "@/common/types/common";
import type { FieldType } from "@/common/types/field";

import { RegisterTheme } from "@/common/themes";
import {
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";

import { fileUploadForm, methodItemForm } from "@/components";

const { t, tm } = useI18n();
const router = useRouter();
const route = useRoute();

const fieldData = ref<FieldType[]>([]);
const itemsData = ref<MethodItemType[]>([]);

const itemPayload = ref<MethodItemPayloadType | null>(null);

const selectedItem = ref<MethodItemType | null>(null);

const itemId = ref<string | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const isLoading = ref(false);

const isEdit = ref(false);
const showItemAdd = ref(false);
const showItemDel = ref(false);
const showModalUpload = ref(false);

const itemLimit = ref<number>(10);
const itemPage = ref<number>(0);
const itemPages = ref<number>(0);
const itemSearch = ref<string>("");
const itemSearched = ref<number>(0);
const itemTotal = ref<number>(0);

const accountId = computed(() => String(route.params.id));
const groupId = computed(() => String(route.params.group));

const menuTabs = computed(() => [
  {
    key: "edit",
    icon: "home-line",
    label: t("common.labels.group"),
  },
  {
    key: "field",
    icon: "field",
    label: t("common.labels.fields"),
  },
  {
    key: "method",
    icon: "method",
    label: t("common.labels.methods"),
  },
  {
    key: "question",
    icon: "form",
    label: t("common.labels.questions"),
  },
]);

const itemDelMessage = computed(() => {
  const message = t("method.texts.del_confirm", {
    concat: selectedItem.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

const tableHeader = computed(() => [
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
]);

function back() {
  router.push(`/${accountId.value}/register/method`);
}

function goToTab(tab: string) {
  router.push(`/${accountId.value}/register/method/${groupId.value}/${tab}`);
}

function fetchFields() {
  methodFieldList(accountId.value, groupId.value, {
    page: 0,
    limit: 500,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        fieldData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchData() {
  methodItemList(accountId.value, groupId.value, {
    limit: itemLimit.value,
    page: itemPage.value,
    search: itemSearch.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        itemPage.value = data.infos.page;
        itemPages.value = data.infos.pages;
        itemTotal.value = data.infos.total;
        itemSearched.value = data.infos.count;
        itemsData.value = [];
        itemsData.value = [...data.list];
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function itemAdd() {
  showItemAdd.value = true;
}

function itemAddCancel() {
  isEdit.value = false;
  showItemAdd.value = false;
  itemPayload.value = null;
  itemId.value = null;
}

function itemAddConfirm() {
  if (!itemPayload.value) return;
  store.commit("SET_LOADING", true);

  methodItemCreate(accountId.value, groupId.value, itemPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemAddCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function itemEdit(value: MethodItemType) {
  itemId.value = value.method_id;
  isEdit.value = true;

  methodItemShow(accountId.value, value.method_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        itemPayload.value = data;
        showItemAdd.value = true;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function itemEditConfirm() {
  if (!itemPayload.value || !itemId.value) return;

  methodItemUpdate(accountId.value, itemId.value, itemPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemAddCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function itemSubmit() {
  if (!isEdit.value) {
    itemAddConfirm();
  } else {
    itemEditConfirm();
  }
}

function itemDel(data: MethodItemType) {
  selectedItem.value = data;
  showItemDel.value = true;
}

function itemDelCancel() {
  selectedItem.value = null;
  showItemDel.value = false;
}

function itemDelConfirm() {
  if (!selectedItem.value) return;

  showItemDel.value = false;
  store.commit("SET_LOADING", true);

  methodItemDelete(accountId.value, selectedItem.value.method_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemDelCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeItemPage(value: number) {
  itemPage.value = value;
  fetchData();
}

function searchItem(value: string) {
  itemSearch.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  showModalUpload.value = false;
  store.commit("SET_LOADING", true);

  methodItemUpload(accountId.value, groupId.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  methodItemModelCSV(accountId.value, groupId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  if (groupId.value) {
    fetchData();
    fetchFields();
  }
});
</script>

<template>
  <register-theme tab-active="method">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="method" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.method") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" active="method" @selected="goToTab($event)">
        <template #content>
          <div class="title">
            {{ t("common.labels.methods") }}
          </div>

          <baseTable
            :header="tableHeader"
            :data="itemsData"
            :loading="isLoading"
            :options="true"
            :edit="true"
            :del="true"
            :paginated="true"
            :upload="true"
            :limit="itemLimit"
            :page="itemPage"
            :pages="itemPages"
            :total="itemTotal"
            :searched="itemSearched"
            @change:page="changeItemPage"
            @searching="searchItem"
            @btn:add="itemAdd()"
            @btn:edit="itemEdit"
            @btn:del="itemDel"
            @btn:upload="uploadCsv()"
          >
          </baseTable>
        </template>
      </icon-menu>
    </template>
  </register-theme>

  <base-modal
    width="80%"
    :title="
      isEdit ? t('method.texts.edit_method') : t('method.texts.add_method')
    "
    :show="showItemAdd"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.confirm')"
    :button-close="t('common.labels.cancel')"
    @close="itemAddCancel()"
    @confirm="itemSubmit()"
  >
    <template #content>
      <method-item-form
        width="100%"
        :data="itemPayload"
        :fields="fieldData"
        @update-data="itemPayload = $event"
      />
    </template>
  </base-modal>

  <base-modal
    :show="showModalUpload"
    :title="
      t('common.labels.upload', {
        concat: t('common.labels.method'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.send')"
    :button-close="t('common.labels.cancel')"
    @close="uploadCancel()"
    @confirm="uploadConfirm()"
  >
    <template #content>
      <file-upload-form
        :extensions="fileExtensions"
        :model="true"
        @update-data="fileUpload = $event"
        @model-download="downloadModel()"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showItemDel"
    :title="t('method.texts.del_method')"
    :message="itemDelMessage"
    @cancel="itemDelCancel()"
    @confirm="itemDelConfirm()"
  />
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
